import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

// Create axios instance
const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
    async (config) => {
        // Get Clerk token
        const token = await window.Clerk?.session?.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response.data,
    (error) => {
        const message = error.response?.data?.message || error.message || 'An error occurred';
        return Promise.reject(new Error(message));
    }
);

// ==================== ADMIN ENDPOINTS ====================

// Dashboard Stats
export const getDashboardStats = () => apiClient.get('/admin/stats');

// Users
export const getUsers = (params) => apiClient.get('/admin/users', { params });
export const getUser = (userId) => apiClient.get(`/admin/users/${userId}`);
export const exportUserEmails = async (filter) => {
    const response = await apiClient.get('/admin/users/export/emails', {
        params: { filter },
        responseType: 'blob'
    });
    return response;
};
export const updateUser = (userId, data) => apiClient.patch(`/admin/users/${userId}`, data);
export const banUser = (userId, reason) => apiClient.post(`/admin/users/${userId}/ban`, { reason });
export const unbanUser = (userId) => apiClient.post(`/admin/users/${userId}/unban`);

// Account Types
export const addUserAccountType = (userId, accountType) =>
    apiClient.post(`/admin/users/${userId}/account-types/add`, { accountType });
export const removeUserAccountType = (userId, accountType) =>
    apiClient.post(`/admin/users/${userId}/account-types/remove`, { accountType });

// Push Notifications
export const sendPushToUser = (userId, data) =>
    apiClient.post(`/admin/users/${userId}/push-notification`, data);

// Username Management
export const changeUsername = (userId, newUsername, reason) =>
    apiClient.post(`/admin/users/${userId}/change-username`, { newUsername, reason });

// Hard Delete (Admin Dev Cleanup)
export const getUserForHardDelete = (userId) =>
    apiClient.get(`/admin/users/${userId}/hard-delete`);
export const hardDeleteUser = (userId) =>
    apiClient.delete(`/admin/users/${userId}/hard-delete`);

// Analytics Exclusion
export const toggleAnalyticsExclusion = (userId, exclude) =>
    apiClient.post(`/admin/users/${userId}/analytics-exclusion`, { exclude });

// Environment management
export const addUserToEnvironment = (userId, environment) =>
    apiClient.post(`/admin/users/${userId}/environments/add`, { environment });

export const removeUserFromEnvironment = (userId, environment) =>
    apiClient.post(`/admin/users/${userId}/environments/remove`, { environment });

export const setUserEnvironments = (userId, environments) =>
    apiClient.put(`/admin/users/${userId}/environments`, { environments });

export const getUsersByEnvironment = (environment) =>
    apiClient.get(`/admin/environments/users?environment=${environment}`);

// ==================== MAINTENANCE ====================

// Get user stats discrepancies
export const getUserStatsDiscrepancies = (limit = 100) =>
    apiClient.get(`/admin/maintenance/user-stats-discrepancies?limit=${limit}`);

// Reconcile user stats
export const reconcileUserStats = (userIds) =>
    apiClient.post('/admin/maintenance/reconcile-user-stats', { userIds });

// Get post stats discrepancies
export const getPostStatsDiscrepancies = (limit = 100) =>
    apiClient.get(`/admin/maintenance/post-stats-discrepancies?limit=${limit}`);

// Reconcile post stats
export const reconcilePostStats = (postIds) =>
    apiClient.post('/admin/maintenance/reconcile-post-stats', { postIds });

// Sync username cache
export const syncUsernameCache = () =>
    apiClient.post('/admin/maintenance/sync-username-cache');

// Account Type Applications (unified for verified and community)
export const getAccountTypeApplications = (params) =>
    apiClient.get('/admin/account-type-applications', { params });
export const getAccountTypeApplicationStats = () =>
    apiClient.get('/admin/account-type-applications/stats');
export const getAccountTypeApplication = (applicationId) =>
    apiClient.get(`/admin/account-type-applications/${applicationId}`);
export const updateAccountTypeApplication = (applicationId, data) =>
    apiClient.patch(`/admin/account-type-applications/${applicationId}`, data);

// Alias for verification-specific pages
export const updateVerificationApplication = updateAccountTypeApplication;
export const getVerificationApplication = getAccountTypeApplication;

// Community requests (filter by accountType=community)
export const getCommunityRequests = (params) =>
    apiClient.get('/admin/account-type-applications', { params: { ...params, accountType: 'community' } });

// Reports
export const getReports = (params) => apiClient.get('/admin/reports', { params });
export const getReport = (reportId) => apiClient.get(`/admin/reports/${reportId}`);
export const updateReport = (reportId, data) => apiClient.patch(`/admin/reports/${reportId}`, data);

// Content Moderation
export const deletePost = (postId, reason) => apiClient.delete(`/admin/posts/${postId}`, { data: { reason } });
export const deleteComment = (commentId, reason) =>
    apiClient.delete(`/admin/comments/${commentId}`, { data: { reason } });

// Reserved Usernames
export const getReservedUsernames = (params) =>
    apiClient.get('/admin/reserved-usernames', { params });
export const createReservedUsername = (data) =>
    apiClient.post('/admin/reserved-usernames', data);
export const bulkCreateReservedUsernames = (usernames) =>
    apiClient.post('/admin/reserved-usernames/bulk', { usernames });
export const deleteReservedUsername = (reservedId) =>
    apiClient.delete(`/admin/reserved-usernames/${reservedId}`);
export const searchReservedUsernames = (query) =>
    apiClient.get('/admin/reserved-usernames/search', { params: { query } });

// Analytics
export const getAnalytics = (params) => apiClient.get('/admin/analytics', { params });

// System
export const clearCache = (pattern) => apiClient.post('/admin/cache/clear', { pattern });
export const triggerJob = (data) => apiClient.post('/admin/jobs/trigger', data);
export const getAllPermissions = () => apiClient.get('/admin/permissions');
export const updatePermissionConfig = (accountType, updates) =>
    apiClient.patch(`/admin/permissions/${accountType}`, updates);

// ==================== SECURITY ENDPOINTS ====================

// Platform Config
export const getPlatformConfig = () => apiClient.get('/admin/config');
export const updatePlatformConfig = (key, value) =>
    apiClient.patch('/admin/config', { key, value });
export const toggleSignupsPaused = (paused, message) =>
    apiClient.post('/admin/config/signups', { paused, message });

// IP Blocking
export const getBlockedIPs = (params) => apiClient.get('/admin/security/blocked-ips', { params });
export const blockIP = (data) => apiClient.post('/admin/security/blocked-ips', data);
export const unblockIP = (ipAddress) =>
    apiClient.delete('/admin/security/blocked-ips', { data: { ipAddress } });
export const blockUserIPs = (userId, data) =>
    apiClient.post(`/admin/security/users/${userId}/block-ips`, data);

// Session Statistics
export const getSessionStats = () => apiClient.get('/admin/security/sessions/stats');
export const getUserSessions = (userId) => apiClient.get(`/admin/security/sessions/user/${userId}`);
export const getSessionsByIP = (ipAddress) =>
    apiClient.get('/admin/security/sessions/ip', { params: { ipAddress } });

// ==================== SYSTEM POPUPS ====================

export const getPopups = (params) => apiClient.get('/admin/popups', { params });
export const getPopup = (popupId) => apiClient.get(`/admin/popups/${popupId}`);
export const createPopup = (data) => apiClient.post('/admin/popups', data);
export const updatePopup = (popupId, data) => apiClient.patch(`/admin/popups/${popupId}`, data);
export const togglePopupActive = (popupId, isActive) =>
    apiClient.patch(`/admin/popups/${popupId}/toggle`, { isActive });
export const deletePopup = (popupId) => apiClient.delete(`/admin/popups/${popupId}`);

// ==================== SUPPORT TICKETS ====================

export const getSupportTickets = (params) => apiClient.get('/admin/support-tickets', { params });
export const getSupportTicketById = (ticketId) => apiClient.get(`/admin/support-tickets/${ticketId}`);
export const getSupportTicketStats = () => apiClient.get('/admin/support-tickets/stats');
export const createSupportTicket = (data) => apiClient.post('/admin/support-tickets', data);
export const updateSupportTicket = (ticketId, data) => apiClient.patch(`/admin/support-tickets/${ticketId}`, data);
export const resolveSupportTicket = (ticketId, data) =>
    apiClient.post(`/admin/support-tickets/${ticketId}/resolve`, data);
export const closeSupportTicket = (ticketId, data) =>
    apiClient.post(`/admin/support-tickets/${ticketId}/close`, data);

// Support Ticket Tags
export const getSupportTicketTags = () => apiClient.get('/admin/support-ticket-tags');
export const getAllSupportTicketTags = () => apiClient.get('/admin/support-ticket-tags/all');
export const createSupportTicketTag = (data) => apiClient.post('/admin/support-ticket-tags', data);
export const updateSupportTicketTag = (tagId, data) =>
    apiClient.patch(`/admin/support-ticket-tags/${tagId}`, data);
export const toggleSupportTicketTagStatus = (tagId, isActive) =>
    apiClient.patch(`/admin/support-ticket-tags/${tagId}/toggle`, { isActive });
export const seedDefaultSupportTicketTags = () => apiClient.post('/admin/support-ticket-tags/seed');

// ==================== BASKETBALL API CACHE ====================

const BASKETBALL_API_URL = import.meta.env.VITE_BASKETBALL_API_URL || 'http://localhost:3002/api/v1';

// Create basketball API client
const basketballApiClient = axios.create({
    baseURL: BASKETBALL_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor for basketball API
basketballApiClient.interceptors.request.use(
    async (config) => {
        const token = await window.Clerk?.session?.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for basketball API
basketballApiClient.interceptors.response.use(
    (response) => response.data,
    (error) => {
        const message = error.response?.data?.message || error.message || 'An error occurred';
        return Promise.reject(new Error(message));
    }
);

// Basketball Cache Management
export const getBasketballCacheStatus = () => basketballApiClient.get('/admin/cache/status');
export const clearAllBasketballCache = () => basketballApiClient.post('/admin/cache/clear');
export const clearBasketballCachePattern = (pattern) =>
    basketballApiClient.post('/admin/cache/clear/pattern', { pattern });
export const clearTeamCache = (teamId) => basketballApiClient.post(`/admin/cache/clear/team/${teamId}`);
export const clearGameCache = (gameId) => basketballApiClient.post(`/admin/cache/clear/game/${gameId}`);
export const clearStandingsCache = () => basketballApiClient.post('/admin/cache/clear/standings');
export const clearLiveGamesCache = () => basketballApiClient.post('/admin/cache/clear/live-games');
export const clearCompetitionsCache = () => basketballApiClient.post('/admin/cache/clear/competitions');
export const clearAllTeamsCache = () => basketballApiClient.post('/admin/cache/clear/all-teams');
export const clearAllGamesCache = () => basketballApiClient.post('/admin/cache/clear/all-games');

// ==================== BASKETBALL GAME MANAGEMENT ====================

// Games
export const getBasketballGames = (competitionId) =>
    basketballApiClient.get(`/admin/games/${competitionId}`);
export const getBasketballGameDetails = (matchId) =>
    basketballApiClient.get(`/admin/games/detail/${matchId}`);
export const sendCustomGamePush = (matchId, data) =>
    basketballApiClient.post(`/admin/games/${matchId}/push/custom`, data);
export const triggerStandardGamePush = (matchId, data) =>
    basketballApiClient.post(`/admin/games/${matchId}/push/trigger`, data);
export const forceStopGame = (matchId, data) =>
    basketballApiClient.post(`/admin/games/${matchId}/force-stop`, data);

// ==================== BASKETBALL IMAGE UPLOAD ====================

export const uploadPersonImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    const token = await window.Clerk?.session?.getToken();

    const response = await fetch(`${BASKETBALL_API_URL}/admin/upload/person-image`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to upload image');
    }

    return response.json();
};

export const getUploadStatus = () =>
    basketballApiClient.get('/admin/upload/status');

// ==================== BASKETBALL CUSTOM PEOPLE ====================

export const getCustomPeople = (type) =>
    basketballApiClient.get('/admin/custom-people', { params: type ? { type } : {} });
export const searchCustomPeople = (query, type) =>
    basketballApiClient.get('/admin/custom-people/search', { params: { q: query, type } });
export const getCustomPerson = (personId) =>
    basketballApiClient.get(`/admin/custom-people/${personId}`);
export const createCustomPerson = (data) =>
    basketballApiClient.post('/admin/custom-people', data);
export const updateCustomPerson = (personId, data) =>
    basketballApiClient.patch(`/admin/custom-people/${personId}`, data);
export const deleteCustomPerson = (personId) =>
    basketballApiClient.delete(`/admin/custom-people/${personId}`);

// ==================== BASKETBALL CUSTOM ROSTER ====================

export const getTeamsInCompetition = (competitionId) =>
    basketballApiClient.get(`/admin/teams/competition/${competitionId}`);
export const getTeamCustomRoster = (teamId) =>
    basketballApiClient.get(`/admin/teams/${teamId}/custom-roster`);
export const addToTeamRoster = (teamId, customPersonId) =>
    basketballApiClient.post(`/admin/teams/${teamId}/custom-roster`, { customPersonId });
export const removeFromTeamRoster = (teamId, personId) =>
    basketballApiClient.delete(`/admin/teams/${teamId}/custom-roster/${personId}`);

// ==================== BASKETBALL GENIUS SEARCH ====================

export const searchGeniusPlayers = (query) =>
    basketballApiClient.get('/admin/genius/players/search', { params: { q: query } });
export const searchGeniusStaff = (query) =>
    basketballApiClient.get('/admin/genius/staff/search', { params: { q: query } });

// ==================== BASKETBALL ROSTER CONFIG ====================

export const getRosterMode = () =>
    basketballApiClient.get('/admin/config/roster-mode');
export const setRosterMode = (useCustomRoster) =>
    basketballApiClient.patch('/admin/config/roster-mode', { useCustomRoster });

// ==================== BASKETBALL COMPETITIONS ====================

export const getBasketballCompetitions = () =>
    basketballApiClient.get('/admin/competitions');

// ==================== BASKETBALL STANDINGS MANAGEMENT ====================

export const getAdminStandings = (competitionId) =>
    basketballApiClient.get(`/admin/standings/${competitionId}`);
export const setManualStandingsOverride = (competitionId, enabled) =>
    basketballApiClient.patch(`/admin/standings/${competitionId}/override`, { enabled });
export const saveManualStandings = (competitionId, rows) =>
    basketballApiClient.post(`/admin/standings/${competitionId}/save`, { rows });
export const seedManualStandingsFromApi = (competitionId) =>
    basketballApiClient.post(`/admin/standings/${competitionId}/seed`);
export const clearManualStandings = (competitionId) =>
    basketballApiClient.delete(`/admin/standings/${competitionId}`);

// ==================== NOTIFICATIONS ====================

export const sendCustomNotification = (data) => apiClient.post('/admin/notifications/send', data);
export const getNotificationLogs = (params) => apiClient.get('/admin/notifications/logs', { params });

export default apiClient;

